import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, GripVertical } from 'lucide-react';
import api, { LeadStage } from '../../services/api';

export default function AdminLeadStages() {
  const [stages, setStages] = useState<LeadStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStage, setEditingStage] = useState<LeadStage | null>(null);
  const [formData, setFormData] = useState({ name: '', color: '#3b82f6', probability: 10, order: 1 });

  useEffect(() => {
    const fetchStages = async () => {
      try {
        const data = await api.getLeadStages();
        setStages(data);
      } catch (error) {
        console.error('Failed to fetch stages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStages();
  }, []);

  const handleSubmit = async () => {
    try {
      if (editingStage) {
        await api.updateLeadStage(editingStage.id, formData);
      } else {
        await api.createLeadStage(formData);
      }
      const data = await api.getLeadStages();
      setStages(data);
      setShowModal(false);
      setEditingStage(null);
      setFormData({ name: '', color: '#3b82f6', probability: 10, order: stages.length + 1 });
    } catch (error) {
      console.error('Failed to save stage:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this stage?')) {
      try {
        await api.deleteLeadStage(id);
        setStages(stages.filter(s => s.id !== id));
      } catch (error) {
        console.error('Failed to delete stage:', error);
      }
    }
  };

  const openEdit = (stage: LeadStage) => {
    setEditingStage(stage);
    setFormData({ name: stage.name, color: stage.color, probability: stage.probability, order: stage.order });
    setShowModal(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Stages</h1>
          <p className="text-gray-500 mt-1">Configure lead pipeline stages.</p>
        </div>
        <button onClick={() => { setEditingStage(null); setFormData({ name: '', color: '#3b82f6', probability: 10, order: stages.length + 1 }); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          <Plus className="w-5 h-5" /> Add Stage
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">Order</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">Stage</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">Color</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">Probability</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {stages.sort((a, b) => a.order - b.order).map((stage) => (
              <tr key={stage.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="font-medium text-gray-900">{stage.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <input type="color" value={stage.color} disabled className="w-10 h-10 rounded cursor-not-allowed" />
                </td>
                <td className="px-6 py-4 text-gray-700">{stage.probability}%</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(stage)} className="p-2 hover:bg-gray-100 rounded-lg"><Pencil className="w-4 h-4 text-gray-500" /></button>
                    <button onClick={() => handleDelete(stage.id)} className="p-2 hover:bg-gray-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">{editingStage ? 'Edit Stage' : 'Add Stage'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="w-12 h-12 rounded cursor-pointer" />
                  <input type="text" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Probability (%)</label>
                <input type="number" min="0" max="100" value={formData.probability} onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingStage ? 'Save Changes' : 'Add Stage'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
